// Path: src/pages/TradingData/index.jsx
// Trading Data — Wizard: Pick Entry → Pick Exit → Add Companions → Add Filters → Configure → Explore
// P&L %, Average, Drawdown, Run-up divided by 100. STATUS column removed.

import React, { useState, useRef } from 'react';
import {
  Box, Typography, Button, TextField, Stack, Paper, Chip, IconButton,
  alpha, Snackbar, Alert, Tooltip,
  Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tabs, Tab, Select, MenuItem, FormControl,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  TableChart as TableChartIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  FilterAlt as FilterAltIcon,
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  Code as CodeIcon,
  FileDownload as FileDownloadIcon,
  UploadFile as UploadFileIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';

import { ASSETS, TIMEFRAMES, TIMEFRAME_LABELS, SESSIONS, MONO } from './IndicatorConfigs';
import { Pill, Label } from './SharedComponents';
import SignalPickerModal from './SignalPickerModal';

const FILTER_COLOR = '#f59e0b';
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SLIPPAGE = 0.05;
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function getDefaultDates() {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const y = et.getFullYear();
  const mo = et.getMonth() + 1;
  const d = String(et.getDate()).padStart(2, '0');
  let startMo = mo - 3;
  let startY = y;
  if (startMo <= 0) { startMo += 12; startY -= 1; }
  return {
    start: `${startY}-${String(startMo).padStart(2, '0')}-01`,
    end: `${y}-${String(mo).padStart(2, '0')}-${d}`,
  };
}

function getDayOfWeek(isoStr) {
  if (!isoStr) return '—';
  try { const dt = new Date(isoStr); return isNaN(dt.getTime()) ? '—' : DAYS[dt.getDay()]; } catch { return '—'; }
}

function getTradingDayDiff(entryIso, exitIso) {
  if (!entryIso || !exitIso) return '—';
  try {
    const e = new Date(entryIso.split('T')[0]);
    const x = new Date(exitIso.split('T')[0]);
    if (isNaN(e.getTime()) || isNaN(x.getTime())) return '—';
    const diffDays = Math.round((x.getTime() - e.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : '—';
  } catch { return '—'; }
}

function StepChip({ num, label, color, signal, onPick, onClear, enabled }) {
  return (
    <Box sx={{ opacity: enabled ? 1 : 0.3, pointerEvents: enabled ? 'auto' : 'none' }}>
      <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
        <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: signal ? color : alpha(color, 0.12), border: `2px solid ${signal ? color : alpha(color, 0.3)}` }}>
          <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: signal ? '#000' : color, fontFamily: MONO }}>{num}</Typography>
        </Box>
        <Typography sx={{ color: signal ? color : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>{label}</Typography>
        {signal && <CheckCircleIcon sx={{ fontSize: 14, color }} />}
      </Stack>
      {signal ? (
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Chip label={signal} onDelete={onClear} sx={{ height: 28, fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO,
            bgcolor: alpha(color, 0.15), color, border: `1px solid ${alpha(color, 0.3)}`,
            '& .MuiChip-label': { px: 1 }, '& .MuiChip-deleteIcon': { color: alpha(color, 0.5) } }} />
          <Button size="small" onClick={onPick} sx={{ fontSize: '0.58rem', fontFamily: MONO, color: alpha('#fff', 0.3), textTransform: 'none' }}>Change</Button>
        </Stack>
      ) : (
        <Button variant="outlined" onClick={onPick} sx={{ borderColor: alpha(color, 0.3), color, fontWeight: 700, fontSize: '0.7rem',
          fontFamily: MONO, textTransform: 'none', borderRadius: 1.5, px: 3, '&:hover': { borderColor: color, bgcolor: alpha(color, 0.08) } }}>
          Pick {label} →
        </Button>
      )}
    </Box>
  );
}

export default function TradingData() {
  // 'auto' = pick entry/exit signals → backend correlates trades.
  // 'upload' = user provides Entry Time + Exit Time per row in an .xlsx file.
  const [dataSource, setDataSource] = useState('auto');
  const [uploadedTrades, setUploadedTrades] = useState([]);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [uploadedRange, setUploadedRange] = useState(null);
  const fileInputRef = useRef(null);

  const [entrySignal, setEntrySignal] = useState(null);
  const [exitSignal, setExitSignal] = useState(null);
  const [companions, setCompanions] = useState([]);
  const [companionMode, setCompanionMode] = useState('smart');
  const [filters, setFilters] = useState([]);
  const [direction, setDirection] = useState('LONG');
  const [asset, setAsset] = useState('NQ');
  const [timeframe, setTimeframe] = useState('1m');
  const [session, setSession] = useState('ETH');
  const [startDate, setStartDate] = useState(getDefaultDates().start);
  const [endDate, setEndDate] = useState(getDefaultDates().end);
  const [entryTimeStart, setEntryTimeStart] = useState('00:00');
  const [entryTimeEnd, setEntryTimeEnd] = useState('23:59');
  const [limit, setLimit] = useState('0');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerRole, setPickerRole] = useState(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const EXPLORE_URL = `${API_BASE}/strategies/api/signals/explore-trades/`;
  const EXPLORE_FROM_ROWS_URL = `${API_BASE}/strategies/api/signals/explore-trades-from-rows/`;
  const DISCOVER_URL = `${API_BASE}/strategies/api/signals/discover-strategies/`;
  const [discovering, setDiscovering] = useState(false);
  const [minWinRate, setMinWinRate] = useState('80');
  const [minTrades, setMinTrades] = useState('5');
  const [discoveredStrategies, setDiscoveredStrategies] = useState(null);
  const [discoveredTab, setDiscoveredTab] = useState(0);
  const [copiedConfigIdx, setCopiedConfigIdx] = useState(null);
  const [configModal, setConfigModal] = useState({ open: false, json: null, stratName: null, copied: false });

  const openPicker = (role) => { setPickerRole(role); setPickerOpen(true); };
  const closePicker = () => { setPickerOpen(false); setPickerRole(null); };

  const handlePick = (result) => {
    if (pickerRole === 'entry') {
      if (typeof result === 'object' && result?.signal) {
        setEntrySignal(result.signal);
        setTimeframe(result.timeframe);
        setAsset(result.asset);
        setSession(result.session);
      } else {
        setEntrySignal(typeof result === 'string' ? result : result?.preceding || result);
      }
    }
    else if (pickerRole === 'exit') {
      setExitSignal(typeof result === 'object' && result?.signal ? result.signal : (typeof result === 'string' ? result : result?.preceding || result));
    }
    else if (pickerRole === 'companion') {
      if (typeof result === 'object' && result?.signal) {
        const key = `${result.signal}|${result.timeframe}|${result.asset}|${result.session}`;
        setCompanions(prev => prev.some(c => `${c.signal}|${c.timeframe}|${c.asset}|${c.session}` === key) ? prev : [...prev, result]);
      } else {
        const sig = typeof result === 'string' ? result : result?.preceding || result;
        setCompanions(prev => prev.some(c => c.signal === sig && c.timeframe === timeframe && c.asset === asset && c.session === session) ? prev : [...prev, { signal: sig, timeframe, asset, session }]);
      }
    }
    else if (pickerRole === 'filter') {
      if (typeof result === 'object' && result?.preceding) {
        setFilters(prev => [...prev, { preceding_signal: result.preceding, exclude_signal: result.exclude, type: 'sequence_exclusion_with_equality' }]);
      } else if (typeof result === 'string') {
        setFilters(prev => [...prev, { preceding_signal: result, exclude_signal: null, type: 'sequence_exclusion_with_equality' }]);
      }
    }
  };

  const removeCompanion = (idx) => setCompanions(prev => prev.filter((_, i) => i !== idx));
  const removeFilter = (idx) => setFilters(prev => prev.filter((_, i) => i !== idx));

  const isFilterMode = pickerRole === 'filter';
  const isCompanionMode = pickerRole === 'companion';
  const roleColor = pickerRole === 'entry' ? '#22c55e' : pickerRole === 'exit' ? '#ef4444' : pickerRole === 'companion' ? '#00d4ff' : FILTER_COLOR;
  const roleLabel = pickerRole === 'entry' ? 'ENTRY' : pickerRole === 'exit' ? 'EXIT' : pickerRole === 'companion' ? 'COMPANION' : 'FILTER';
  const isUploadMode = dataSource === 'upload';
  const tradesReady = isUploadMode ? uploadedTrades.length > 0 : !!(entrySignal && exitSignal);
  const canExplore = tradesReady;
  const downstreamEnabled = tradesReady;  // companions/filters/configure step gating

  const payloadFilters = filters.filter(f => f.preceding_signal).map(f => ({
    type: f.type, preceding_signal: f.preceding_signal,
    ...(f.exclude_signal ? { exclude_signal: f.exclude_signal } : {}),
  }));

  const timeRestriction = (() => {
    if (!entryTimeStart || !entryTimeEnd) return null;
    const [hs, ms] = entryTimeStart.split(':').map(Number);
    const [he, me] = entryTimeEnd.split(':').map(Number);
    if (isNaN(hs) || isNaN(ms) || isNaN(he) || isNaN(me)) return null;
    return { hour_start: hs, minute_start: ms, hour_end: he, minute_end: me };
  })();

  const commonPayload = {
    companion_signals: companions.map(c => ({
      signal: c.signal, timeframe: c.timeframe, asset: c.asset, session: c.session,
    })),
    direction, timeframe, asset,
    trading_hour: session, start_date: startDate, end_date: endDate,
    limit: parseInt(limit) || 0,
    companion_mode: companionMode,
    ...(payloadFilters.length > 0 ? { filters: payloadFilters } : {}),
    ...(timeRestriction ? { time_restriction: timeRestriction } : {}),
  };

  const payload = isUploadMode
    ? { ...commonPayload, trades: uploadedTrades }
    : { ...commonPayload, entry_signal: entrySignal || '', exit_signal: exitSignal || '' };

  const handleExplore = async () => {
    if (!canExplore) return;
    console.group('%c📊 TRADE EXPLORER', 'color: #a855f7; font-weight: bold; font-size: 13px;');
    console.log('%cPayload:', 'color: #00d4ff;', payload);
    if (payloadFilters.length) console.log('%cFilters (AND — ALL must pass):', 'color: #f59e0b; font-weight: bold;', payloadFilters);
    console.groupEnd();
    setLoading(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(isUploadMode ? EXPLORE_FROM_ROWS_URL : EXPLORE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data = null;
      try { data = text ? JSON.parse(text) : null; }
      catch {
        // Server returned non-JSON (usually a 404/500 HTML page). Surface the
        // status + a snippet of the body so the cause is visible at a glance.
        const snippet = text.replace(/\s+/g, ' ').slice(0, 200);
        setError(`HTTP ${res.status} — server returned non-JSON: ${snippet || '<empty body>'}`);
        return;
      }
      if (res.ok) {
        setResult(data);
        const filterMsg = data.filters_applied > 0 ? ` (${data.entries_filtered_out} rejected by filters)` : '';
        const oobMsg = data.rows_skipped_out_of_bounds > 0 ? ` (${data.rows_skipped_out_of_bounds} skipped by date/time/day filter)` : '';
        setSnackbar({ open: true, message: `${data.trade_count} trades found${filterMsg}${oobMsg}`, severity: 'success' });
      }
      else setError((data && data.error) || `HTTP ${res.status}`);
    } catch (err) { setError(`Network error: ${err.message}`); }
    setLoading(false);
  };

  // ── File upload (.xlsx / .csv) ────────────────────────────
  // Supports two formats:
  //   A. Paired columns:  "Entry Time" + "Exit Time"
  //   B. Row-based (TradingView "List of Trades"-style):
  //        "Type" column with Entry/Exit values + a Date/Time column,
  //        optionally grouped by "Trade #". Direction (LONG/SHORT) is
  //        inferred from the Type cell and the UI direction is auto-synced
  //        when the file is unambiguous.
  const parseUploadedFile = async (file) => {
    setUploadError('');
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      // For CSV, leave cells as raw strings so the regex parser interprets
      // them as naive wall-clock. For Excel, cellDates: true gives Date objects
      // whose UTC components carry the original wall-clock value.
      const isCsv = /\.csv$/i.test(file.name);
      const wb = XLSX.read(buf, { type: 'array', cellDates: !isCsv });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error('Workbook is empty');
      const json = XLSX.utils.sheet_to_json(ws, { defval: '', raw: true });
      if (!json.length) throw new Error('No rows found in the first sheet');

      const pad = (n) => String(n).padStart(2, '0');
      const norm = (s) => String(s || '').replace(/[\s_/#-]+/g, '').toLowerCase();

      // Parse a cell into a naive local ISO string ("YYYY-MM-DDTHH:MM:SS").
      // Excel/CSV date cells come back as JS Date objects whose UTC components
      // carry the original wall-clock value (xlsx convention). String cells get
      // a regex parse first to avoid timezone misinterpretation, with new Date()
      // as a last-resort fallback.
      const toLocalIso = (cell) => {
        if (cell == null || cell === '') return null;
        if (cell instanceof Date && !isNaN(cell.getTime())) {
          return `${cell.getUTCFullYear()}-${pad(cell.getUTCMonth() + 1)}-${pad(cell.getUTCDate())}`
               + `T${pad(cell.getUTCHours())}:${pad(cell.getUTCMinutes())}:${pad(cell.getUTCSeconds())}`;
        }
        const s = String(cell).trim();
        // Excel serial date: numeric value = days since 1899-12-30. CSVs
        // exported from Excel/Sheets often arrive this way (cellDates is off
        // for CSV input). Bound to ~year 1900-2173 so we don't misclassify
        // small numeric IDs.
        if (/^\d+(\.\d+)?$/.test(s)) {
          const serial = parseFloat(s);
          if (serial > 1 && serial < 100000) {
            const ms = Math.round((serial - 25569) * 86400 * 1000);
            const d = new Date(ms);
            if (!isNaN(d.getTime())) {
              return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
                   + `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
            }
          }
        }
        const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[T\s]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/);
        if (m) {
          return `${m[1]}-${pad(m[2])}-${pad(m[3])}T${pad(m[4])}:${pad(m[5])}:${pad(m[6] || '00')}`;
        }
        const d = new Date(s);
        // Reject parses that produce wildly out-of-range years (e.g. JS
        // accepts "46136" as year-only and returns year 46136). 1970-2100
        // is a permissive sanity bound.
        if (!isNaN(d.getTime()) && d.getFullYear() >= 1970 && d.getFullYear() <= 2100) {
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
               + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        }
        return null;
      };

      // Infer { kind, direction } from a Type-column value. Tiered match —
      // specific compounds first, then generic verbs, then bare buy/sell.
      const classifyType = (raw) => {
        if (raw == null || raw === '') return { kind: 'unknown', direction: null };
        const s = String(raw).toLowerCase().trim();
        // Compound patterns (handle "sell short" before bare "sell" etc.)
        if (/sell\s+short|short\s+sell|open\s+short|enter\s+short|entry\s+short/.test(s))
          return { kind: 'entry', direction: 'SHORT' };
        if (/buy\s+to\s+cover|cover\s+short|close\s+short|exit\s+short/.test(s))
          return { kind: 'exit', direction: 'SHORT' };
        if (/buy\s+to\s+open|open\s+long|enter\s+long|entry\s+long/.test(s))
          return { kind: 'entry', direction: 'LONG' };
        if (/sell\s+to\s+close|close\s+long|exit\s+long/.test(s))
          return { kind: 'exit', direction: 'LONG' };
        // Direction pulled from any "long"/"short" token
        let direction = null;
        if (/\bshort\b/.test(s)) direction = 'SHORT';
        else if (/\blong\b/.test(s)) direction = 'LONG';
        // Generic verbs
        if (/\b(entry|enter|open)\b/.test(s)) return { kind: 'entry', direction };
        if (/\b(exit|close|stop|cover)\b/.test(s)) return { kind: 'exit', direction };
        // Bare buy/sell (assume LONG when unspecified)
        if (/^buy\b/.test(s)) return { kind: 'entry', direction: direction || 'LONG' };
        if (/^sell\b/.test(s)) return { kind: 'exit', direction: direction || 'LONG' };
        return { kind: 'unknown', direction };
      };

      const headerKeys = Object.keys(json[0]);
      const findHeader = (...candidates) =>
        headerKeys.find(k => candidates.some(c => norm(k) === norm(c)))
        || headerKeys.find(k => candidates.some(c => norm(k).includes(norm(c))));

      const entryKey = findHeader('Entry Time', 'EntryTime');
      const exitKey  = findHeader('Exit Time', 'ExitTime');
      const typeKey  = findHeader('Type', 'Action', 'Side');
      const dateKey  = headerKeys.find(k => {
        if (k === entryKey || k === exitKey) return false;
        const n = norm(k);
        return n === 'dateandtime' || n === 'datetime' || n === 'time' || n === 'date'
            || n.includes('dateandtime') || n.includes('datetime');
      });
      const tradeIdKey = findHeader('Trade #', 'TradeNum', 'TradeNumber', 'Trade ID', 'TradeId');

      let rows = [];
      let skippedSrcCount = 0;
      let detectedDirection = null;
      let mode = null;

      // ── Format A: paired Entry Time / Exit Time columns ──
      if (entryKey && exitKey) {
        mode = 'paired';
        for (const r of json) {
          const et = toLocalIso(r[entryKey]);
          const xt = toLocalIso(r[exitKey]);
          if (!et || !xt) continue;
          rows.push({ entry_time: et, exit_time: xt });
        }
        skippedSrcCount = json.length - rows.length;
      }
      // ── Format B: Type column + Date/Time column ──
      else if (typeKey && dateKey) {
        mode = 'type-rows';
        const enriched = json.map(r => ({
          row: r,
          tradeId: tradeIdKey != null ? String(r[tradeIdKey] ?? '').trim() : null,
          time: toLocalIso(r[dateKey]),
          cls: classifyType(r[typeKey]),
        })).filter(x => x.time);

        const directions = new Set();
        const pushDir = (d) => { if (d) directions.add(d); };

        if (tradeIdKey && enriched.some(x => x.tradeId)) {
          // Group by Trade # — within each group, classify entry/exit.
          const groups = new Map();
          for (const x of enriched) {
            const id = x.tradeId || `__row_${groups.size}`;
            if (!groups.has(id)) groups.set(id, []);
            groups.get(id).push(x);
          }
          for (const [, group] of groups) {
            if (group.length < 2) continue;
            let entry = group.find(x => x.cls.kind === 'entry');
            let exit  = group.find(x => x.cls.kind === 'exit');
            // Fallback: chronological order within the group
            if (!entry || !exit) {
              const sorted = [...group].sort((a, b) => a.time.localeCompare(b.time));
              entry = entry || sorted[0];
              exit  = exit  || sorted[sorted.length - 1];
            }
            if (entry && exit && entry.time && exit.time && entry.time < exit.time) {
              pushDir(entry.cls.direction);
              pushDir(exit.cls.direction);
              rows.push({ entry_time: entry.time, exit_time: exit.time });
            }
          }
          skippedSrcCount = json.length - rows.length * 2;
        } else {
          // No trade ID — chronological state machine.
          const sorted = [...enriched]
            .filter(x => x.cls.kind === 'entry' || x.cls.kind === 'exit')
            .sort((a, b) => a.time.localeCompare(b.time));
          let pending = null;
          for (const x of sorted) {
            if (x.cls.kind === 'entry') {
              pending = x;
            } else if (x.cls.kind === 'exit' && pending && pending.time < x.time) {
              pushDir(pending.cls.direction);
              pushDir(x.cls.direction);
              rows.push({ entry_time: pending.time, exit_time: x.time });
              pending = null;
            }
          }
          skippedSrcCount = json.length - rows.length * 2;
        }

        if (directions.size === 1) detectedDirection = [...directions][0];
        else if (directions.size > 1) detectedDirection = 'MIXED';
      } else {
        throw new Error(
          'Could not find "Entry Time" + "Exit Time" columns, or a "Type" + "Date/Time" pair. '
          + `Detected headers: ${headerKeys.join(', ')}`
        );
      }

      if (!rows.length) throw new Error('No valid entry/exit pairs found in the file');

      const sorted = [...rows].sort((a, b) => a.entry_time.localeCompare(b.entry_time));

      // Auto-sync UI direction when the file is unambiguous.
      if (detectedDirection === 'LONG' || detectedDirection === 'SHORT') {
        setDirection(detectedDirection);
      }

      setUploadedTrades(rows);
      setUploadedFileName(file.name);
      setUploadedRange({
        first: sorted[0].entry_time.slice(0, 10),
        last: sorted[sorted.length - 1].entry_time.slice(0, 10),
        count: rows.length,
        skipped: skippedSrcCount,
        mode,
        direction: detectedDirection,
      });
    } catch (err) {
      setUploadedTrades([]);
      setUploadedFileName('');
      setUploadedRange(null);
      setUploadError(err.message || String(err));
    }
  };

  const clearUpload = () => {
    setUploadedTrades([]);
    setUploadedFileName('');
    setUploadedRange(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── XLSX Export ────────────────────────────────────────────
  const handleExcel = () => {
    if (!result?.trades?.length) return;
    const cn = result.companion_signals || [];
    const ccMeta = result.companion_columns || [];
    const cleanDt = (iso) => {
      if (!iso) return '';
      const p = iso.split('T');
      return `${p[0] || ''} ${(p[1] || '').replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', '').slice(0, 8)}`;
    };

    // Build Excel column names with context metadata
    const excelCompHeaders = cn.map((sig, i) => {
      const cc = ccMeta[i];
      if (!cc) return sig;
      const sub = [cc.timeframe, cc.asset, cc.session].filter(Boolean);
      return sub.length ? `${sig} (${sub.join(' · ')})` : sig;
    });

    const rows = result.trades.map(t => {
      const ex = t.excursion;
      const day = getDayOfWeek(t.entry_time);
      const tDayDiff = getTradingDayDiff(t.entry_time, t.exit_time);
      const avg = t.pnl_pct != null ? +((t.pnl_pct - SLIPPAGE) / 100).toFixed(6) : '';

      const row = {
        '#': t.trade_num,
        'Trade': result.direction,
        'Date': (t.entry_time?.split('T') || [''])[0],
      };

      cn.forEach((c, i) => { row[excelCompHeaders[i]] = cleanDt(t.companions?.[c]?.time); });

      Object.assign(row, {
        'Entry': cleanDt(t.entry_time),
        'Entry $': t.entry_price,
        'Exit': cleanDt(t.exit_time),
        'Exit $': t.exit_price,
        'Duration': t.duration_human,
        'P&L': t.pnl_points,
        'P&L %': t.pnl_pct != null ? +(t.pnl_pct / 100).toFixed(6) : '',
        'Days': day,
        'TDay Diff': tDayDiff,
        'Average': avg,
        'Drawdown': ex?.drawdown_pct != null ? +(ex.drawdown_pct / 100).toFixed(6) : '',
        'Lowest Point': ex?.lowest_point ?? '',
        'Drawdown Time': ex?.drawdown_time ? cleanDt(ex.drawdown_time) : '',
        'Run-up': ex?.runup_pct != null ? +(ex.runup_pct / 100).toFixed(6) : '',
        'Highest Point': ex?.highest_point ?? '',
        'Highest Point Time': ex?.runup_time ? cleanDt(ex.runup_time) : '',
      });

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trades');
    const stem = isUploadMode
      ? (uploadedFileName.replace(/\.[^.]+$/, '') || 'uploaded')
      : entrySignal;
    XLSX.writeFile(wb, `trades_${stem}_${asset}_${startDate}.xlsx`);
  };

  // ── Strategy Discovery ────────────────────────────────────
  const handleDiscover = async () => {
    if (!canExplore || !companions.length) return;
    setDiscovering(true); setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const discoverPayload = {
        ...payload,
        min_win_rate: parseFloat(minWinRate) || 80,
        min_trades: parseInt(minTrades) || 5,
        // Remove time restriction — the engine scans all windows
        time_restriction: undefined,
      };
      delete discoverPayload.time_restriction;
      const res = await fetch(DISCOVER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(discoverPayload),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.strategies?.length) {
          setDiscoveredStrategies(data);
          _buildDiscoveryExcel(data);
          setSnackbar({ open: true, message: `${data.strategy_count} strategies found! Excel downloaded.`, severity: 'success' });
        } else {
          setDiscoveredStrategies(null);
          setSnackbar({ open: true, message: 'No strategies met the criteria. Try lowering win rate or min trades.', severity: 'warning' });
        }
      } else {
        setError(data.error || `HTTP ${res.status}`);
      }
    } catch (err) { setError(`Network error: ${err.message}`); }
    setDiscovering(false);
  };

  const _buildDiscoveryExcel = (data) => {
    const wb = XLSX.utils.book_new();
    const cleanDt = (iso) => {
      if (!iso) return '';
      const p = iso.split('T');
      return `${p[0] || ''} ${(p[1] || '').replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', '').slice(0, 5)}`;
    };

    data.strategies.forEach((strat, idx) => {
      const rows = [];
      const tw = strat.time_window;
      const conds = strat.conditions || [];
      const cond0 = conds[0] || {};

      // Header info
      rows.push([`${tw.start}\u2013${tw.end}`, strat.instruction]);
      rows.push([`${strat.win_rate}% WR \u2014 ${strat.trade_count} trades`]);
      rows.push([]);
      rows.push([`Condition type: ${strat.type}`, `Instructions \u2014 take the ${data.direction} trade only when ALL conditions are met:`]);

      if (strat.type === 'CONFIRMATION') {
        rows.push([`  1.  Only take trades where Entry Time is between ${tw.start} and ${tw.end} ET`]);
        rows.push([`  2.  CONFIRMATION \u2014 ${cond0.signal_label} must have fired ${cond0.offset_min}\u2013${cond0.offset_max} minutes before entry`]);
        rows.push([`      Meaning: Entry Time minus ${cond0.signal_label} timestamp = ${cond0.offset_min}\u2013${cond0.offset_max} min`]);
        rows.push([`  3.  Both conditions must be met before taking the trade.`]);
      } else {
        const cond1 = conds[1] || {};
        rows.push([`  1.  Only take trades where Entry Time is between ${tw.start} and ${tw.end} ET`]);
        rows.push([`  2.  REQUIRED \u2014 ${cond0.signal_label} must have fired ${cond0.offset_min}\u2013${cond0.offset_max} minutes before ${cond1.signal_label}`]);
        rows.push([`      Meaning: ${cond1.signal_label} timestamp minus ${cond0.signal_label} timestamp = ${cond0.offset_min}\u2013${cond0.offset_max} min`]);
        rows.push([`      Both signals must have fired at or before your entry.`]);
        rows.push([`  3.  Both conditions must be met before taking the trade.`]);
      }

      rows.push([]);
      rows.push(['Trade List']);

      // Build companion column headers from the strategy trades
      const companionSigs = data.companion_signals || [];
      const companionCols = data.companion_columns || [];
      const compHeaders = companionSigs.map((sig, i) => {
        const cc = companionCols[i];
        if (!cc) return sig;
        const sub = [cc.timeframe, cc.asset, cc.session].filter(Boolean);
        return sub.length ? `${sig} (${sub.join(' \u00b7 ')})` : sig;
      });

      // Table header
      const header = ['Date', 'Entry', 'Exit', ...compHeaders, 'Entry $', 'Exit $', 'P&L %'];
      if (strat.type === 'CONFIRMATION') {
        header.push(`${cond0.signal_label} \u2192 Entry (mins)`);
      } else {
        const cond1 = conds[1] || {};
        header.push(`${cond0.signal_label} \u2192 ${cond1.signal_label} (mins)`);
      }
      header.push('Result');
      rows.push(header);

      // Trade rows
      let wins = 0, losses = 0;
      strat.trades.forEach(t => {
        const row = [
          (t.entry_time?.split('T') || [''])[0],
          cleanDt(t.entry_time),
          cleanDt(t.exit_time),
        ];

        // Companion columns
        companionSigs.forEach(sig => {
          const comp = t.companions?.[sig];
          row.push(comp?.time ? cleanDt(comp.time) : '');
        });

        row.push(t.entry_price);
        row.push(t.exit_price);
        row.push(t.pnl_pct != null ? `${(t.pnl_pct / 100).toFixed(3)}%` : '');

        // Offset column
        if (strat.type === 'CONFIRMATION') {
          const comp = t.companions?.[cond0.signal];
          if (comp?.time && t.entry_time) {
            try {
              const diff = (new Date(t.entry_time) - new Date(comp.time)) / 60000;
              row.push(`${diff.toFixed(1)} mins`);
            } catch { row.push(''); }
          } else { row.push(''); }
        } else {
          const cond1 = conds[1] || {};
          const compA = t.companions?.[cond0.signal];
          const compB = t.companions?.[cond1.signal];
          if (compA?.time && compB?.time) {
            try {
              const diff = (new Date(compB.time) - new Date(compA.time)) / 60000;
              row.push(`${diff.toFixed(1)} mins`);
            } catch { row.push(''); }
          } else { row.push(''); }
        }

        const result = t.status === 'WIN' ? 'WIN' : 'LOSS';
        row.push(result);
        if (result === 'WIN') wins++; else losses++;
        rows.push(row);
      });

      // Summary row
      const summaryRow = [`Total: ${strat.trade_count} trades`, '', '', '', '', '', '', `${wins}W  /  ${losses}L`, `${strat.win_rate}%`];
      rows.push(summaryRow);

      const ws = XLSX.utils.aoa_to_sheet(rows);
      // Truncate sheet name to 31 chars (Excel limit)
      const typeAbbr = strat.type === 'CONFIRMATION' ? 'Conf' : 'Req';
      const twLabel = `${tw.start.replace(':', '')}-${tw.end.replace(':', '')}`;
      const sheetName = `${idx + 1} ${typeAbbr} ${twLabel} WR${Math.round(strat.win_rate)}`.slice(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    if (wb.SheetNames.length === 0) return;
    XLSX.writeFile(wb, `strategies_${entrySignal}_${asset}_${startDate}.xlsx`);
  };

  const generateStrategyConfig = (strat) => {
    const parseTimeStr = (s) => {
      const [h, m] = (s || '0:0').split(':').map(Number);
      return { hour: isNaN(h) ? 0 : h, minute: isNaN(m) ? 0 : m };
    };
    const tw = strat.time_window || {};
    const startT = parseTimeStr(tw.start);
    const endT = parseTimeStr(tw.end);

    // Resolve timeframe/session per condition from its context string ("1m · NQ · ETH")
    const getSignalObj = (c) => {
      let condTf = timeframe, condSession = session;
      if (c.context) {
        const parts = c.context.split(/\s*[·\u00b7]\s*/);
        if (parts.length >= 3) { condTf = parts[0].trim(); condSession = parts[2].trim(); }
        else if (parts.length >= 1) { condTf = parts[0].trim(); }
      }
      return { name: c.signal, timeframe: condTf, trading_hour: condSession };
    };

    const triggerObj = { name: entrySignal, timeframe, trading_hour: session };
    const exitObj = { name: exitSignal, timeframe, trading_hour: session };
    const subStrategies = [];
    const requiredIds = [];
    const confirmationIds = [];

    if (strat.type === 'TRIPLE' || strat.type === 'CONFIRMATION') {
      // Each companion condition is time_bound: companion fires before trigger
      (strat.conditions || []).forEach((c, i) => {
        const sigObj = getSignalObj(c);
        const sid = `C${i + 1}-${c.signal.replace(/[^A-Z0-9]/gi, '-')}-TO-${(entrySignal || 'ENTRY').replace(/[^A-Z0-9]/gi, '-')}`;
        subStrategies.push({
          condition: {
            type: 'time_bound',
            min_minutes: c.offset_min ?? 0,
            max_minutes: c.offset_max ?? 60,
            early_signal: sigObj,
            confirmation_signal: triggerObj,
          },
          strategy_id: sid,
        });
        confirmationIds.push(sid);
      });
    } else {
      // REQUIRED: cond[0] fires sequentially before cond[1]
      const c0 = strat.conditions?.[0] || {};
      const c1 = strat.conditions?.[1] || {};
      const sid = `R1-${(c0.signal || 'SIG-A').replace(/[^A-Z0-9]/gi, '-')}-TO-${(c1.signal || 'SIG-B').replace(/[^A-Z0-9]/gi, '-')}`;
      subStrategies.push({
        condition: {
          type: 'sequence_exclusion_with_equality',
          signals: [
            { ...getSignalObj(c0), order: 1 },
            { ...getSignalObj(c1), order: 2 },
          ],
          exclude_signals: [],
        },
        strategy_id: sid,
      });
      requiredIds.push(sid);
    }

    const twLabel = `${tw.start || ''}\u2013${tw.end || ''}`;
    const desc = `${asset} ${direction} ${entrySignal || ''} | ${strat.type} | ${twLabel} ET | ${strat.win_rate}% WR, ${strat.trade_count} trades`;
    const bulkId = `${asset} ${direction} ${entrySignal || 'STRAT'} | ${strat.type} ${twLabel}`;

    const config = {
      symbol: asset,
      direction,
      is_active: true,
      timeframe,
      active_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      description: desc,
      exit_signal: exitObj,
      trading_hour: session,
      sub_strategies: subStrategies,
      trigger_signal: entrySignal,
      trading_enabled: true,
      bulk_strategy_id: bulkId,
      time_restriction: {
        hour_start: startT.hour,
        minute_start: startT.minute,
        hour_end: endT.hour,
        minute_end: endT.minute,
        timezone: 'America/New_York',
      },
      required_strategies: requiredIds,
      confirmation_strategies: confirmationIds,
      confirmation_time_window_seconds: 30,
    };
    return JSON.stringify(config, null, 2);
  };

  const tfSx = {
    '& .MuiOutlinedInput-root': { bgcolor: alpha('#000', 0.3), fontSize: '0.72rem', fontFamily: MONO, color: '#e2e8f0', borderRadius: 1.5, '& fieldset': { borderColor: alpha('#fff', 0.08) } },
    '& input': { py: 0.7, px: 1 }, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.6)' },
  };

  // ── Grid columns (STATUS removed) ─────────────────────────
  const companionColsMeta = result?.companion_columns || [];
  const companionCols = companionColsMeta.map(() => '190px').join(' ');
  const gridCols = `40px 58px 100px ${companionCols} 190px 88px 190px 88px 62px 68px 68px 78px 52px 68px 62px 78px 135px 62px 78px 135px`;

  // Build companion header labels with context metadata
  const companionHeaders = companionColsMeta.map(cc => {
    const parts = [cc.signal];
    const sub = [cc.timeframe, cc.asset, cc.session].filter(Boolean);
    if (sub.length) parts.push(`(${sub.join(' · ')})`);
    return parts.join(' ');
  });
  // Fallback: if backend didn't send companion_columns, use signal names
  const companionHeaderLabels = companionHeaders.length
    ? companionHeaders
    : (result?.companion_signals || []);

  const headerLabels = [
    '#', 'TRADE', 'DATE',
    ...companionHeaderLabels,
    'ENTRY', 'ENTRY $', 'EXIT', 'EXIT $', 'DUR', 'P&L', 'P&L %',
    'DAY', 'TDAY', 'AVG',
    'DD %', 'LOW PT', 'DD TIME',
    'RU %', 'HIGH PT', 'RU TIME',
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', marginTop: '50px' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TableChartIcon sx={{ color: '#a855f7', fontSize: 28 }} />
          <Box>
            <Typography sx={{ color: '#e2e8f0', fontSize: '1.3rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Automatic Data Generation</Typography>
            <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.65rem', fontFamily: MONO, letterSpacing: '0.1em' }}>
              {isUploadMode ? 'UPLOAD TRADES → CONFIGURE → EXPLORE' : 'PICK SIGNALS → CONFIGURE → EXPLORE TRADES'}
            </Typography>
          </Box>
        </Stack>
        <Chip label="F12 → Console" size="small" sx={{ bgcolor: alpha('#f59e0b', 0.12), color: '#f59e0b', border: `1px solid ${alpha('#f59e0b', 0.3)}`, fontWeight: 700, fontSize: '0.58rem', fontFamily: MONO }} />
      </Stack>

      {/* ═══ WIZARD ═══ */}
      <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha('#a855f7', 0.1)}`, borderRadius: 2.5, p: 2.5, mb: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Data Source toggle */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
              <Typography sx={{ color: alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>DATA SOURCE</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Pill active={dataSource === 'auto'} activeColor="#a855f7"
                onClick={() => setDataSource('auto')}
                sx={{ py: 0.5, px: 1.4, fontSize: '0.62rem' }}>
                Auto-generate from signals
              </Pill>
              <Pill active={dataSource === 'upload'} activeColor="#a855f7"
                onClick={() => setDataSource('upload')}
                sx={{ py: 0.5, px: 1.4, fontSize: '0.62rem' }}>
                Upload trades file
              </Pill>
            </Stack>
          </Box>

          {/* Steps 1+2: Entry/Exit (auto mode) OR file upload (upload mode) */}
          {!isUploadMode ? (
            <>
              <StepChip num="1" label="ENTRY" color="#22c55e" signal={entrySignal} onPick={() => openPicker('entry')} onClear={() => setEntrySignal(null)} enabled={true} />
              <StepChip num="2" label="EXIT" color="#ef4444" signal={exitSignal} onPick={() => openPicker('exit')} onClear={() => setExitSignal(null)} enabled={!!entrySignal} />
            </>
          ) : (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: uploadedTrades.length ? '#22c55e' : alpha('#22c55e', 0.12), border: `2px solid ${uploadedTrades.length ? '#22c55e' : alpha('#22c55e', 0.3)}` }}>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: uploadedTrades.length ? '#000' : '#22c55e', fontFamily: MONO }}>1</Typography>
                </Box>
                <Typography sx={{ color: uploadedTrades.length ? '#22c55e' : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>UPLOAD ENTRY/EXIT TIMES</Typography>
                {uploadedTrades.length > 0 && <CheckCircleIcon sx={{ fontSize: 14, color: '#22c55e' }} />}
              </Stack>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => parseUploadedFile(e.target.files?.[0])}
                style={{ display: 'none' }}
              />

              {uploadedTrades.length === 0 ? (
                <Button onClick={() => fileInputRef.current?.click()}
                  startIcon={<UploadFileIcon sx={{ fontSize: 18 }} />}
                  sx={{ fontFamily: MONO, fontSize: '0.72rem', fontWeight: 700, textTransform: 'none',
                    color: '#22c55e', border: `1px dashed ${alpha('#22c55e', 0.4)}`, borderRadius: 1.5,
                    px: 3, py: 1.2,
                    '&:hover': { borderColor: '#22c55e', bgcolor: alpha('#22c55e', 0.06) } }}>
                  Choose .xlsx / .csv (Entry/Exit Time columns OR Type + Date)
                </Button>
              ) : (
                <Paper elevation={0} sx={{ bgcolor: alpha('#22c55e', 0.04), border: `1px solid ${alpha('#22c55e', 0.2)}`, borderRadius: 1.5, p: 1.2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap">
                    <InsertDriveFileIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                    <Typography sx={{ color: '#e2e8f0', fontSize: '0.72rem', fontFamily: MONO, fontWeight: 700 }}>
                      {uploadedFileName}
                    </Typography>
                    <Chip label={`${uploadedRange?.count || uploadedTrades.length} trades`} size="small"
                      sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO,
                        bgcolor: alpha('#22c55e', 0.15), color: '#22c55e', '& .MuiChip-label': { px: 0.6 } }} />
                    {uploadedRange && (
                      <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.62rem', fontFamily: MONO }}>
                        {uploadedRange.first} → {uploadedRange.last}
                      </Typography>
                    )}
                    {uploadedRange?.skipped > 0 && (
                      <Chip label={`${uploadedRange.skipped} skipped`} size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontFamily: MONO,
                          bgcolor: alpha('#f59e0b', 0.12), color: '#f59e0b', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    {uploadedRange?.direction === 'LONG' && (
                      <Chip label="auto LONG" size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontFamily: MONO,
                          bgcolor: alpha('#22c55e', 0.12), color: '#22c55e', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    {uploadedRange?.direction === 'SHORT' && (
                      <Chip label="auto SHORT" size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontFamily: MONO,
                          bgcolor: alpha('#ef4444', 0.12), color: '#ef4444', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    {uploadedRange?.direction === 'MIXED' && (
                      <Chip label="mixed direction — using UI toggle" size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontFamily: MONO,
                          bgcolor: alpha('#f59e0b', 0.18), color: '#f59e0b', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    {uploadedRange?.mode === 'type-rows' && (
                      <Chip label="type-rows" size="small"
                        sx={{ height: 16, fontSize: '0.55rem', fontFamily: MONO,
                          bgcolor: alpha('#a855f7', 0.12), color: '#a855f7', '& .MuiChip-label': { px: 0.5 } }} />
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.4 }}>
                      <Button size="small" onClick={() => fileInputRef.current?.click()}
                        sx={{ fontSize: '0.58rem', fontFamily: MONO, color: alpha('#fff', 0.5), textTransform: 'none', minWidth: 0, px: 1 }}>
                        Replace
                      </Button>
                      <IconButton size="small" onClick={clearUpload} sx={{ color: alpha('#fff', 0.4), p: 0.4 }}>
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Paper>
              )}

              {uploadError && (
                <Typography sx={{ color: '#ef4444', fontSize: '0.65rem', fontFamily: MONO, mt: 0.8 }}>
                  {uploadError}
                </Typography>
              )}
            </Box>
          )}

          {/* Step 3: Companions */}
          <Box sx={{ opacity: downstreamEnabled ? 1 : 0.3, pointerEvents: downstreamEnabled ? 'auto' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: companions.length ? '#00d4ff' : alpha('#00d4ff', 0.12), border: `2px solid ${companions.length ? '#00d4ff' : alpha('#00d4ff', 0.3)}` }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: companions.length ? '#000' : '#00d4ff', fontFamily: MONO }}>3</Typography>
              </Box>
              <Typography sx={{ color: companions.length ? '#00d4ff' : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>COMPANIONS</Typography>
              <Chip label="optional" size="small" sx={{ height: 14, fontSize: '0.45rem', bgcolor: alpha('#fff', 0.04), color: alpha('#fff', 0.2), '& .MuiChip-label': { px: 0.4 } }} />
              <Box sx={{ ml: 'auto' }}>
                <Stack direction="row" spacing={0.3}>
                  {['simple', 'smart'].map(m => (
                    <Pill key={m} active={companionMode === m} activeColor={m === 'smart' ? '#22c55e' : '#94a3b8'} onClick={() => setCompanionMode(m)} sx={{ py: 0.2, px: 1, fontSize: '0.52rem', minWidth: 0 }}>
                      {m === 'smart' ? '🧠 Smart' : 'Simple'}
                    </Pill>
                  ))}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {companions.map((c, i) => (
                <Chip key={`${c.signal}-${c.timeframe}-${c.asset}-${c.session}-${i}`}
                  label={`${c.signal} (${c.timeframe} · ${c.asset} · ${c.session})`}
                  size="small" onDelete={() => removeCompanion(i)} sx={{ height: 24, fontSize: '0.65rem', fontWeight: 600, fontFamily: MONO,
                  bgcolor: alpha('#00d4ff', 0.12), color: '#00d4ff', border: `1px solid ${alpha('#00d4ff', 0.25)}`,
                  '& .MuiChip-label': { px: 0.7 }, '& .MuiChip-deleteIcon': { fontSize: 14, color: alpha('#00d4ff', 0.5) } }} />
              ))}
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 12 }} />} onClick={() => openPicker('companion')}
                sx={{ fontSize: '0.58rem', fontFamily: MONO, color: alpha('#00d4ff', 0.6), textTransform: 'none',
                  border: `1px dashed ${alpha('#00d4ff', 0.2)}`, borderRadius: 1, px: 1.5, '&:hover': { bgcolor: alpha('#00d4ff', 0.06), borderColor: alpha('#00d4ff', 0.4) } }}>
                Add
              </Button>
            </Stack>
          </Box>

          {/* ═══ SEQUENCE FILTERS ═══ */}
          <Box sx={{ opacity: downstreamEnabled ? 1 : 0.3, pointerEvents: downstreamEnabled ? 'auto' : 'none' }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: filters.length ? FILTER_COLOR : alpha(FILTER_COLOR, 0.12), border: `2px solid ${filters.length ? FILTER_COLOR : alpha(FILTER_COLOR, 0.3)}` }}>
                <FilterAltIcon sx={{ fontSize: 11, color: filters.length ? '#000' : FILTER_COLOR }} />
              </Box>
              <Typography sx={{ color: filters.length ? FILTER_COLOR : alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>FILTERS</Typography>
              <Chip label="optional" size="small" sx={{ height: 14, fontSize: '0.45rem', bgcolor: alpha('#fff', 0.04), color: alpha('#fff', 0.2), '& .MuiChip-label': { px: 0.4 } }} />
            </Stack>
            <Stack spacing={0.8}>
              {filters.map((f, idx) => (
                <Paper key={idx} elevation={0} sx={{ bgcolor: alpha(FILTER_COLOR, 0.04), border: `1px solid ${alpha(FILTER_COLOR, 0.12)}`, borderRadius: 1.5, px: 1, py: 0.7 }}>
                  <Stack direction="row" alignItems="center" spacing={0.6} flexWrap="wrap">
                    <Chip label={f.preceding_signal} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO,
                      bgcolor: alpha('#22c55e', 0.12), color: '#22c55e', border: `1px solid ${alpha('#22c55e', 0.25)}`, '& .MuiChip-label': { px: 0.6 } }} />
                    <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>→ entry</Typography>
                    {f.exclude_signal && (
                      <>
                        <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>, no</Typography>
                        <Chip label={f.exclude_signal} size="small" sx={{ height: 22, fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO,
                          bgcolor: alpha('#ef4444', 0.12), color: '#ef4444', border: `1px solid ${alpha('#ef4444', 0.25)}`, '& .MuiChip-label': { px: 0.6 } }} />
                        <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.55rem', fontFamily: MONO }}>between</Typography>
                      </>
                    )}
                    <IconButton size="small" onClick={() => removeFilter(idx)} sx={{ ml: 'auto', color: alpha('#fff', 0.2) }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}
              <Button size="small" startIcon={<FilterAltIcon sx={{ fontSize: 12 }} />} onClick={() => openPicker('filter')}
                sx={{ alignSelf: 'flex-start', fontSize: '0.58rem', fontFamily: MONO, color: alpha(FILTER_COLOR, 0.6),
                  textTransform: 'none', border: `1px dashed ${alpha(FILTER_COLOR, 0.2)}`, borderRadius: 1, px: 1.5,
                  '&:hover': { bgcolor: alpha(FILTER_COLOR, 0.06), borderColor: alpha(FILTER_COLOR, 0.4) } }}>
                Add Filter
              </Button>
            </Stack>
          </Box>

          {/* Step 4: Configure & Explore */}
          <Box sx={{ opacity: canExplore ? 1 : 0.3, pointerEvents: canExplore ? 'auto' : 'none', borderTop: `1px solid ${alpha('#fff', 0.06)}`, pt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1.5 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha('#a855f7', 0.12), border: `2px solid ${alpha('#a855f7', 0.3)}` }}>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#a855f7', fontFamily: MONO }}>4</Typography>
              </Box>
              <Typography sx={{ color: alpha('#fff', 0.45), fontSize: '0.72rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.08em' }}>CONFIGURE & EXPLORE</Typography>
            </Stack>

            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1}>
                <Box sx={{ flex: 1 }}><Label>Direction</Label>
                  <Stack direction="row" spacing={0.3}>{['LONG', 'SHORT'].map(d => <Pill key={d} active={direction === d} activeColor={d === 'LONG' ? '#22c55e' : '#ef4444'} onClick={() => setDirection(d)}>{d}</Pill>)}</Stack>
                </Box>
                <Box sx={{ flex: 1 }}><Label>Asset</Label>
                  <Stack direction="row" spacing={0.3}>{ASSETS.map(a => <Pill key={a} active={asset === a} activeColor="#a855f7" onClick={() => setAsset(a)}>{a}</Pill>)}</Stack>
                </Box>
                <Box sx={{ flex: 1 }}><Label>Timeframe</Label>
                  <FormControl fullWidth size="small">
                    <Select
                      value={timeframe}
                      onChange={(e) => setTimeframe(e.target.value)}
                      sx={{
                        fontFamily: MONO, fontSize: '0.72rem', fontWeight: 700,
                        color: '#a855f7', borderRadius: 1,
                        '& .MuiSelect-select': { py: 0.6 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#a855f7', 0.25) },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#a855f7', 0.5) },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
                        '& .MuiSelect-icon': { color: alpha('#a855f7', 0.6) },
                        bgcolor: alpha('#a855f7', 0.06),
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            bgcolor: '#0d1117', border: `1px solid ${alpha('#a855f7', 0.2)}`,
                            borderRadius: 2, mt: 0.5,
                          },
                        },
                      }}
                    >
                      {TIMEFRAMES.map(t => (
                        <MenuItem key={t} value={t} sx={{
                          fontFamily: MONO, fontSize: '0.72rem', fontWeight: 600,
                          color: alpha('#fff', 0.7),
                          '&.Mui-selected': { color: '#a855f7', bgcolor: alpha('#a855f7', 0.1) },
                          '&:hover': { bgcolor: alpha('#a855f7', 0.07) },
                        }}>
                          {TIMEFRAME_LABELS[t] || t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ flex: 1 }}><Label>Session</Label>
                  <Stack direction="row" spacing={0.3}>{SESSIONS.map(s => <Pill key={s} active={session === s} activeColor="#a855f7" onClick={() => setSession(s)}>{s}</Pill>)}</Stack>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Box sx={{ flex: 1 }}><Label>Start Date</Label><TextField size="small" fullWidth type="date" value={startDate} onChange={e => setStartDate(e.target.value)} sx={tfSx} /></Box>
                <Box sx={{ flex: 1 }}><Label>End Date</Label><TextField size="small" fullWidth type="date" value={endDate} onChange={e => setEndDate(e.target.value)} sx={tfSx} /></Box>
                <Box sx={{ width: 100 }}><Label>Entry From (ET)</Label><TextField size="small" fullWidth type="time" value={entryTimeStart} onChange={e => setEntryTimeStart(e.target.value)} placeholder="00:00" sx={tfSx} /></Box>
                <Box sx={{ width: 100 }}><Label>Entry To (ET)</Label><TextField size="small" fullWidth type="time" value={entryTimeEnd} onChange={e => setEntryTimeEnd(e.target.value)} placeholder="23:59" sx={tfSx} /></Box>
                <Box sx={{ width: 80 }}><Label>Limit</Label><TextField size="small" fullWidth value={limit} onChange={e => setLimit(e.target.value)} sx={tfSx} /></Box>
              </Stack>

              <Paper elevation={0} sx={{ bgcolor: alpha('#000', 0.4), border: `1px solid ${alpha('#a855f7', 0.1)}`, borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  borderBottom: `1px solid ${alpha('#fff', 0.04)}`, bgcolor: alpha('#fff', 0.02) }}>
                  <Stack direction="row" alignItems="center" spacing={0.6}>
                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: canExplore ? '#22c55e' : '#f59e0b' }} />
                    <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.5rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em' }}>PAYLOAD</Typography>
                  </Stack>
                  <Tooltip title="Copy"><IconButton size="small" onClick={() => navigator.clipboard.writeText(JSON.stringify(payload, null, 2))} sx={{ color: alpha('#fff', 0.3) }}><CopyIcon sx={{ fontSize: 11 }} /></IconButton></Tooltip>
                </Box>
                <Box sx={{ px: 1.5, py: 0.6, maxHeight: 130, overflowY: 'auto' }}>
                  <Typography component="pre" sx={{ color: alpha('#fff', 0.25), fontSize: '0.55rem', fontFamily: MONO, lineHeight: 1.5, whiteSpace: 'pre-wrap', m: 0 }}>
                    {JSON.stringify(payload, null, 2)}
                  </Typography>
                </Box>
              </Paper>

              <Button fullWidth variant="contained" onClick={handleExplore} disabled={loading || discovering || !canExplore}
                startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                sx={{ bgcolor: '#a855f7', color: '#fff', fontWeight: 800, fontSize: '0.8rem', fontFamily: MONO, letterSpacing: '0.1em', borderRadius: 1.5, py: 1.2,
                  textTransform: 'none', boxShadow: `0 4px 20px ${alpha('#a855f7', 0.35)}`, '&:hover': { bgcolor: '#9333ea' },
                  '&.Mui-disabled': { bgcolor: alpha('#a855f7', 0.2), color: alpha('#fff', 0.4) } }}>
                {loading ? 'EXPLORING...' : 'EXPLORE TRADES'}
              </Button>

              {/* Strategy Discovery (auto mode only — discover endpoint requires entry/exit signals) */}
              {!isUploadMode && companions.length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha('#f59e0b', 0.15)}` }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography sx={{ color: '#f59e0b', fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.1em' }}>STRATEGY DISCOVERY</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.52rem', fontFamily: MONO }}>Min WR%</Typography>
                      <TextField value={minWinRate} onChange={e => setMinWinRate(e.target.value)} size="small"
                        sx={{ width: 50, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
                      <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.52rem', fontFamily: MONO }}>Min Trades</Typography>
                      <TextField value={minTrades} onChange={e => setMinTrades(e.target.value)} size="small"
                        sx={{ width: 50, ...tfSx }} inputProps={{ style: { textAlign: 'center' } }} />
                    </Stack>
                  </Stack>
                  <Button fullWidth variant="contained" onClick={handleDiscover} disabled={discovering || loading || !canExplore}
                    sx={{ bgcolor: '#f59e0b', color: '#000', fontWeight: 800, fontSize: '0.75rem', fontFamily: MONO, letterSpacing: '0.08em', borderRadius: 1.5, py: 1,
                      textTransform: 'none', boxShadow: `0 4px 18px ${alpha('#f59e0b', 0.35)}`, '&:hover': { bgcolor: '#d97706' },
                      '&.Mui-disabled': { bgcolor: alpha('#f59e0b', 0.2), color: alpha('#000', 0.4) } }}>
                    {discovering ? 'DISCOVERING STRATEGIES...' : 'DISCOVER STRATEGIES & DOWNLOAD EXCEL'}
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {error && (
        <Paper elevation={0} sx={{ bgcolor: alpha('#ef4444', 0.08), border: `1px solid ${alpha('#ef4444', 0.3)}`, borderRadius: 1.5, p: 1.5, mb: 2 }}>
          <Typography sx={{ color: '#ef4444', fontSize: '0.72rem', fontWeight: 600, fontFamily: MONO }}>{error}</Typography>
        </Paper>
      )}

      {/* ═══ RESULTS TABLE ═══ */}
      {result && (
        <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha('#a855f7', 0.1)}`, borderRadius: 2.5, overflow: 'hidden' }}>
          {result.summary && (
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${alpha('#fff', 0.06)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography sx={{ color: '#e2e8f0', fontSize: '1rem', fontWeight: 800, fontFamily: MONO }}>Trade Log</Typography>
                <Chip label={`${result.trade_count} trades`} size="small" sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha('#a855f7', 0.15), color: '#a855f7' }} />
                <Chip label={`${asset} · ${timeframe} · ${session}`} size="small" sx={{ height: 20, fontSize: '0.58rem', fontWeight: 700, bgcolor: alpha('#00d4ff', 0.12), color: '#00d4ff' }} />
                {result.filters_applied > 0 && (
                  <Chip icon={<FilterAltIcon sx={{ fontSize: 10 }} />}
                    label={`${result.filters_applied} filter${result.filters_applied > 1 ? 's' : ''} · ${result.entries_filtered_out || 0} rejected`}
                    size="small" sx={{ height: 20, fontSize: '0.55rem', fontWeight: 700, bgcolor: alpha(FILTER_COLOR, 0.12), color: FILTER_COLOR }} />
                )}
              </Stack>
              <Stack direction="row" spacing={2.5} alignItems="center">
                {[
                  { l: 'Win Rate', v: `${result.summary.win_rate}%`, c: result.summary.win_rate >= 50 ? '#22c55e' : '#ef4444' },
                  { l: 'Total P&L', v: `${result.summary.total_pnl_points > 0 ? '+' : ''}${result.summary.total_pnl_points}`, c: result.summary.total_pnl_points >= 0 ? '#22c55e' : '#ef4444' },
                  { l: 'Avg', v: `${result.summary.avg_pnl_points > 0 ? '+' : ''}${result.summary.avg_pnl_points}`, c: result.summary.avg_pnl_points >= 0 ? '#22c55e' : '#ef4444' },
                  { l: 'W/L', v: `${result.summary.wins}/${result.summary.losses}`, c: '#94a3b8' },
                ].map(s => (
                  <Box key={s.l} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.48rem', fontWeight: 600, fontFamily: MONO, letterSpacing: '0.1em' }}>{s.l}</Typography>
                    <Typography sx={{ color: s.c, fontSize: '0.82rem', fontWeight: 800, fontFamily: MONO }}>{s.v}</Typography>
                  </Box>
                ))}
                <Tooltip title="Excel"><IconButton size="small" onClick={handleExcel} sx={{ color: alpha('#fff', 0.3), '&:hover': { color: '#22c55e' } }}><DownloadIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
              </Stack>
            </Box>
          )}

          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1800 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: gridCols,
                px: 1.5, py: 0.7, bgcolor: alpha('#fff', 0.02), borderBottom: `1px solid ${alpha('#fff', 0.08)}`, position: 'sticky', top: 0, zIndex: 1 }}>
                {headerLabels.map((h, i) => {
                  const isDD = ['DD %', 'LOW PT', 'DD TIME'].includes(h);
                  const isRU = ['RU %', 'HIGH PT', 'RU TIME'].includes(h);
                  const isNew = ['DAY', 'TDAY', 'AVG'].includes(h);
                  const hColor = isDD ? alpha('#ef4444', 0.5) : isRU ? alpha('#22c55e', 0.5) : isNew ? alpha('#f59e0b', 0.6) : alpha('#fff', 0.4);
                  return (
                    <Typography key={`${h}-${i}`} sx={{ color: hColor, fontSize: '0.52rem', fontWeight: 700, fontFamily: MONO, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</Typography>
                  );
                })}
              </Box>

              <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                {result.trades.map((t, idx) => {
                  const ep = t.entry_time?.split('T') || ['', ''];
                  const xp = t.exit_time?.split('T') || ['', ''];
                  const fmtDt = (p) => { const d = p[0] || ''; const tm = (p[1] || '').replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', '').slice(0, 8); return d ? `${d} ${tm}` : '—'; };
                  const fmtIso = (iso) => { if (!iso) return '—'; const p = iso.split('T'); return fmtDt(p); };
                  const cn = result.companion_signals || [];
                  const ex = t.excursion;

                  const pnlPctDiv = t.pnl_pct != null ? (t.pnl_pct / 100) : null;
                  const avg = t.pnl_pct != null ? ((t.pnl_pct - SLIPPAGE) / 100) : null;
                  const ddPct = ex?.drawdown_pct != null ? (ex.drawdown_pct / 100) : null;
                  const ruPct = ex?.runup_pct != null ? (ex.runup_pct / 100) : null;
                  const avgColor = avg != null ? (avg >= 0 ? '#22c55e' : '#ef4444') : alpha('#fff', 0.2);
                  const day = getDayOfWeek(t.entry_time);
                  const tDayDiff = getTradingDayDiff(t.entry_time, t.exit_time);

                  return (
                    <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: gridCols,
                      px: 1.5, py: 0.55, borderBottom: `1px solid ${alpha('#fff', 0.03)}`, '&:hover': { bgcolor: alpha('#a855f7', 0.03) }, alignItems: 'center' }}>
                      <Typography sx={{ color: alpha('#fff', 0.2), fontSize: '0.62rem', fontFamily: MONO }}>#{t.trade_num}</Typography>

                      {/* TRADE (direction) */}
                      <Typography sx={{ color: result.direction === 'LONG' ? '#22c55e' : '#ef4444', fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO }}>{result.direction}</Typography>

                      {/* DATE */}
                      <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem', fontFamily: MONO }}>{ep[0] || '—'}</Typography>

                      {/* Companions */}
                      {cn.map(c => {
                        const comp = t.companions?.[c];
                        if (!comp) return <Typography key={c} sx={{ color: alpha('#fff', 0.15), fontSize: '0.58rem', fontFamily: MONO }}>—</Typography>;
                        const cp = comp.time?.split('T') || ['', ''];
                        return <Typography key={c} sx={{ color: '#00d4ff', fontSize: '0.58rem', fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtDt(cp)}</Typography>;
                      })}

                      {/* Entry / Exit */}
                      <Typography sx={{ color: '#22c55e', fontSize: '0.58rem', fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtDt(ep)}</Typography>
                      <Typography sx={{ color: '#22c55e', fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO }}>{t.entry_price?.toLocaleString()}</Typography>
                      <Typography sx={{ color: '#ef4444', fontSize: '0.58rem', fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtDt(xp)}</Typography>
                      <Typography sx={{ color: '#ef4444', fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO }}>{t.exit_price?.toLocaleString()}</Typography>
                      <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.6rem', fontFamily: MONO }}>{t.duration_human}</Typography>

                      {/* P&L */}
                      <Typography sx={{ color: (t.pnl_points || 0) >= 0 ? '#22c55e' : '#ef4444', fontSize: '0.62rem', fontWeight: 700, fontFamily: MONO }}>
                        {t.pnl_points != null ? `${t.pnl_points > 0 ? '+' : ''}${t.pnl_points.toFixed(2)}` : '—'}
                      </Typography>

                      {/* P&L % (÷100) */}
                      <Typography sx={{ color: (t.pnl_pct || 0) >= 0 ? '#22c55e' : '#ef4444', fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO }}>
                        {pnlPctDiv != null ? `${pnlPctDiv > 0 ? '+' : ''}${pnlPctDiv.toFixed(4)}%` : '—'}
                      </Typography>

                      {/* DAY */}
                      <Typography sx={{ color: alpha('#f59e0b', 0.7), fontSize: '0.55rem', fontWeight: 600, fontFamily: MONO, whiteSpace: 'nowrap' }}>
                        {day}
                      </Typography>

                      {/* TDAY DIFF */}
                      <Typography sx={{ color: alpha('#f59e0b', 0.6), fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO, textAlign: 'center' }}>
                        {tDayDiff}
                      </Typography>

                      {/* AVERAGE (÷100) */}
                      <Typography sx={{ color: avgColor, fontSize: '0.6rem', fontWeight: 700, fontFamily: MONO }}>
                        {avg != null ? `${avg.toFixed(4)}%` : '—'}
                      </Typography>

                      {/* DRAWDOWN % (÷100) */}
                      <Typography sx={{ color: '#ef4444', fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO }}>
                        {ddPct != null ? `${ddPct.toFixed(4)}%` : '—'}
                      </Typography>
                      <Typography sx={{ color: alpha('#ef4444', 0.7), fontSize: '0.58rem', fontFamily: MONO }}>
                        {ex?.lowest_point != null ? ex.lowest_point.toLocaleString() : '—'}
                      </Typography>
                      <Typography sx={{ color: alpha('#ef4444', 0.5), fontSize: '0.55rem', fontFamily: MONO, whiteSpace: 'nowrap' }}>
                        {ex?.drawdown_time ? fmtIso(ex.drawdown_time) : '—'}
                      </Typography>

                      {/* RUN-UP % (÷100) */}
                      <Typography sx={{ color: '#22c55e', fontSize: '0.58rem', fontWeight: 700, fontFamily: MONO }}>
                        {ruPct != null ? `${ruPct.toFixed(4)}%` : '—'}
                      </Typography>
                      <Typography sx={{ color: alpha('#22c55e', 0.7), fontSize: '0.58rem', fontFamily: MONO }}>
                        {ex?.highest_point != null ? ex.highest_point.toLocaleString() : '—'}
                      </Typography>
                      <Typography sx={{ color: alpha('#22c55e', 0.5), fontSize: '0.55rem', fontFamily: MONO, whiteSpace: 'nowrap' }}>
                        {ex?.runup_time ? fmtIso(ex.runup_time) : '—'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ═══ DISCOVERED STRATEGIES ═══ */}
      {discoveredStrategies?.strategies?.length > 0 && (
        <Paper elevation={0} sx={{ bgcolor: alpha('#0a0e17', 0.95), border: `1px solid ${alpha('#f59e0b', 0.1)}`, borderRadius: 2.5, p: 2.5, mt: 3 }}>
          {/* Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TrophyIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
              <Typography sx={{ color: '#e2e8f0', fontSize: '0.9rem', fontWeight: 700, fontFamily: MONO }}>
                {discoveredStrategies.strategy_count || 0} Strategies Found
              </Typography>
            </Stack>
            {discoveredTab === 1 && (
              <Button size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  const all = (discoveredStrategies.strategies || []).map(s => JSON.parse(generateStrategyConfig(s)));
                  const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url;
                  a.download = `configs_${entrySignal || 'strategies'}_${asset}_${startDate}.json`;
                  a.click(); URL.revokeObjectURL(url);
                }}
                sx={{ fontSize: '0.62rem', fontFamily: MONO, color: '#22c55e', textTransform: 'none',
                  border: `1px solid ${alpha('#22c55e', 0.3)}`, borderRadius: 1, px: 1.5,
                  '&:hover': { bgcolor: alpha('#22c55e', 0.08) } }}>
                Download All JSON
              </Button>
            )}
          </Stack>

          {/* Tabs */}
          <Tabs value={discoveredTab} onChange={(_, v) => setDiscoveredTab(v)}
            sx={{ mb: 2, minHeight: 32, borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
              '& .MuiTabs-indicator': { bgcolor: '#f59e0b' },
              '& .MuiTab-root': { minHeight: 32, py: 0.5, fontSize: '0.62rem', fontFamily: MONO, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'none', color: alpha('#fff', 0.4) },
              '& .Mui-selected': { color: '#f59e0b !important' } }}>
            <Tab label="Strategies" />
            <Tab label="Configs" />
          </Tabs>

          {/* Tab 0: Accordions */}
          {discoveredTab === 0 && (
            <Stack spacing={1}>
              {discoveredStrategies.strategies.map((strat, idx) => {
                const tw = strat.time_window;
                const cond0 = strat.conditions?.[0] || {};
                const wrColor = strat.win_rate >= 90 ? '#22c55e' : strat.win_rate >= 80 ? '#f59e0b' : '#ef4444';
                return (
                  <Accordion key={idx} disableGutters elevation={0}
                    sx={{ bgcolor: alpha('#fff', 0.02), border: `1px solid ${alpha(wrColor, 0.15)}`, borderRadius: '8px !important',
                      '&::before': { display: 'none' }, '&.Mui-expanded': { borderColor: alpha(wrColor, 0.3) } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: alpha('#fff', 0.3), fontSize: 18 }} />}
                      sx={{ px: 2, py: 0.5, minHeight: 48 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
                        <Chip label={`#${idx + 1}`} size="small" sx={{ height: 20, fontSize: '0.5rem', fontWeight: 800, fontFamily: MONO,
                          bgcolor: alpha(wrColor, 0.12), color: wrColor, '& .MuiChip-label': { px: 0.5 } }} />
                        <Chip label={strat.type} size="small" sx={{ height: 18, fontSize: '0.45rem', fontWeight: 700, fontFamily: MONO,
                          bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', '& .MuiChip-label': { px: 0.5 } }} />
                        <Typography sx={{ color: wrColor, fontSize: '0.78rem', fontWeight: 800, fontFamily: MONO }}>
                          {strat.win_rate}% WR
                        </Typography>
                        <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '0.6rem', fontFamily: MONO }}>
                          {strat.trade_count} trades ({strat.wins}W / {strat.losses}L)
                        </Typography>
                        <Chip label={`${tw.start}\u2013${tw.end}`} size="small" sx={{ height: 18, fontSize: '0.48rem', fontFamily: MONO,
                          bgcolor: alpha('#00d4ff', 0.08), color: '#00d4ff', '& .MuiChip-label': { px: 0.5 } }} />
                        <Typography sx={{ color: alpha('#fff', 0.35), fontSize: '0.52rem', fontFamily: MONO, flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {strat.conditions?.length > 1 ? `${strat.conditions.length} conditions` : cond0.description}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                      <Paper elevation={0} sx={{ bgcolor: alpha('#000', 0.3), borderRadius: 1.5, p: 1.5, mb: 1.5 }}>
                        <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.62rem', fontFamily: MONO, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                          {strat.instruction}
                        </Typography>
                      </Paper>
                      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button size="small" startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
                          onClick={() => {
                            const json = generateStrategyConfig(strat);
                            const name = `${entrySignal || 'STRAT'} ${strat.type} ${tw.start}\u2013${tw.end}`;
                            setConfigModal({ open: true, json, stratName: name, copied: false });
                          }}
                          sx={{ fontSize: '0.62rem', fontFamily: MONO, color: '#a855f7', textTransform: 'none',
                            border: `1px solid ${alpha('#a855f7', 0.3)}`, borderRadius: 1, px: 1.5,
                            '&:hover': { bgcolor: alpha('#a855f7', 0.08) } }}>
                          Generate Config
                        </Button>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Stack>
          )}

          {/* Tab 1: Config Download */}
          {discoveredTab === 1 && (
            <Stack spacing={1.5}>
              {discoveredStrategies.strategies.map((strat, idx) => {
                const tw = strat.time_window;
                const wrColor = strat.win_rate >= 90 ? '#22c55e' : strat.win_rate >= 80 ? '#f59e0b' : '#ef4444';
                const json = generateStrategyConfig(strat);
                const isCopied = copiedConfigIdx === idx;
                const safeFilename = `config_${(entrySignal || 'strat').replace(/[^A-Z0-9]/gi, '_')}_${strat.type}_${(tw.start || '').replace(':', '')}_${(tw.end || '').replace(':', '')}.json`;

                const handleDownload = () => {
                  const blob = new Blob([json], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url; a.download = safeFilename;
                  a.click(); URL.revokeObjectURL(url);
                };
                const handleCopy = () => {
                  navigator.clipboard.writeText(json);
                  setCopiedConfigIdx(idx);
                  setTimeout(() => setCopiedConfigIdx(null), 2000);
                };

                return (
                  <Paper key={idx} elevation={0}
                    sx={{ bgcolor: alpha('#fff', 0.02), border: `1px solid ${alpha(wrColor, 0.12)}`, borderRadius: 1.5, overflow: 'hidden' }}>
                    {/* Row header */}
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ px: 2, py: 1, borderBottom: `1px solid ${alpha('#fff', 0.05)}` }}>
                      <Chip label={`#${idx + 1}`} size="small" sx={{ height: 18, fontSize: '0.5rem', fontWeight: 800, fontFamily: MONO,
                        bgcolor: alpha(wrColor, 0.12), color: wrColor, '& .MuiChip-label': { px: 0.5 } }} />
                      <Chip label={strat.type} size="small" sx={{ height: 16, fontSize: '0.44rem', fontWeight: 700, fontFamily: MONO,
                        bgcolor: alpha('#a855f7', 0.1), color: '#a855f7', '& .MuiChip-label': { px: 0.5 } }} />
                      <Typography sx={{ color: wrColor, fontSize: '0.72rem', fontWeight: 800, fontFamily: MONO }}>{strat.win_rate}% WR</Typography>
                      <Typography sx={{ color: alpha('#fff', 0.4), fontSize: '0.6rem', fontFamily: MONO }}>{strat.trade_count} trades</Typography>
                      <Chip label={`${tw.start}\u2013${tw.end}`} size="small" sx={{ height: 16, fontSize: '0.46rem', fontFamily: MONO,
                        bgcolor: alpha('#00d4ff', 0.08), color: '#00d4ff', '& .MuiChip-label': { px: 0.5 } }} />
                      <Box sx={{ flex: 1 }} />
                      <Button size="small"
                        startIcon={isCopied ? <CheckCircleIcon sx={{ fontSize: 12 }} /> : <CopyIcon sx={{ fontSize: 12 }} />}
                        onClick={handleCopy}
                        sx={{ fontSize: '0.58rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 1.2,
                          color: isCopied ? '#22c55e' : alpha('#fff', 0.5),
                          border: `1px solid ${isCopied ? alpha('#22c55e', 0.4) : alpha('#fff', 0.12)}`,
                          '&:hover': { bgcolor: alpha('#fff', 0.04) } }}>
                        {isCopied ? 'Copied!' : 'Copy'}
                      </Button>
                      <Button size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 12 }} />}
                        onClick={handleDownload}
                        sx={{ fontSize: '0.58rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 1.2,
                          color: '#22c55e', border: `1px solid ${alpha('#22c55e', 0.3)}`,
                          '&:hover': { bgcolor: alpha('#22c55e', 0.06) } }}>
                        Download
                      </Button>
                    </Stack>
                    {/* JSON preview */}
                    <Box component="pre" sx={{
                      m: 0, px: 2, py: 1.5, maxHeight: 220, overflowY: 'auto',
                      color: '#e2e8f0', fontSize: '0.62rem', fontFamily: MONO, lineHeight: 1.6, whiteSpace: 'pre',
                    }}>
                      {json}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Paper>
      )}

      {/* Generate Config modal */}
      <Dialog open={configModal.open} onClose={() => setConfigModal(m => ({ ...m, open: false, copied: false }))}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: '#0a0e17', border: `1px solid ${alpha('#a855f7', 0.3)}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CodeIcon sx={{ color: '#a855f7', fontSize: 18 }} />
            <Typography sx={{ color: '#e2e8f0', fontFamily: MONO, fontSize: '0.82rem', fontWeight: 700 }}>
              Strategy Config &mdash; {configModal.stratName}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={() => setConfigModal(m => ({ ...m, open: false, copied: false }))} sx={{ color: alpha('#fff', 0.3) }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: alpha('#fff', 0.3), fontSize: '0.58rem', fontFamily: MONO, mb: 1.5 }}>
            Copy this config and paste it into the Monaco editor at{' '}
            <span style={{ color: alpha('#fff', 0.55) }}>officer/strategy-config</span>{' '}
            to deploy this strategy.
          </Typography>
          <Box component="pre" sx={{
            bgcolor: alpha('#000', 0.6), border: `1px solid ${alpha('#fff', 0.08)}`,
            borderRadius: 1.5, p: 2, m: 0, overflowX: 'auto', maxHeight: 420, overflowY: 'auto',
            color: '#e2e8f0', fontSize: '0.68rem', fontFamily: MONO, lineHeight: 1.65, whiteSpace: 'pre',
          }}>
            {configModal.json}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button size="small" onClick={() => setConfigModal(m => ({ ...m, open: false, copied: false }))}
            sx={{ fontSize: '0.65rem', fontFamily: MONO, color: alpha('#fff', 0.35), textTransform: 'none' }}>
            Close
          </Button>
          <Button size="small" variant="contained"
            startIcon={configModal.copied ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CopyIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              navigator.clipboard.writeText(configModal.json || '');
              setConfigModal(m => ({ ...m, copied: true }));
              setTimeout(() => setConfigModal(m => ({ ...m, copied: false })), 2000);
            }}
            sx={{ fontSize: '0.65rem', fontFamily: MONO, textTransform: 'none', borderRadius: 1, px: 2,
              bgcolor: configModal.copied ? '#22c55e' : '#a855f7',
              '&:hover': { bgcolor: configModal.copied ? '#16a34a' : '#9333ea' } }}>
            {configModal.copied ? 'Copied!' : 'Copy Config'}
          </Button>
        </DialogActions>
      </Dialog>

      <SignalPickerModal open={pickerOpen} onClose={closePicker} onSelect={handlePick}
        roleLabel={roleLabel} roleColor={roleColor} filterMode={isFilterMode}
        companionMode={isCompanionMode} defaults={{ timeframe, asset, session }} />

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled"
          sx={{ bgcolor: snackbar.severity === 'success' ? '#22c55e' : '#ef4444', fontWeight: 700, fontFamily: MONO, fontSize: '0.78rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
